import { useState, useEffect, FC } from 'react'
import { NavLink, Prompt, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Formik, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { AxiosError } from 'axios'

import { Auth } from 'services/Api'
import { Card as MerinioCard } from 'components/UI'
import { Button, Input, Form } from 'components/Forms'
import Notifications from 'components/Notifications'
import { setLoggedIn } from 'store/actions/auth'
import { openModal } from 'store/actions/modals'
import { Alert, Card } from 'ui'
import { Trans, useTranslation } from 'react-i18next'

type NotificationError = {
  title: string
  type: string
  description: string
  _id: string
}

type InitialValues = {
  username: string
  password: string
}

const Login: FC = () => {
  const dispatch = useDispatch()
  const location = useLocation<{
    emailConfirmation: string
    errorMessage: string
  }>()
  const { co } = useSelector((state: MStore.RootState) => state.auth)

  const [errors, setErrors] = useState<NotificationError[]>([])
  //Set the state of the confirmationAlert, default value is sent from ForgotPassword or ForgotUsername
  const [confirmationAlert, setConfirmationAlert] = useState(location.state?.emailConfirmation)
  const { t } = useTranslation('legacy')

  // Adds a new error notification when given old array & `newError = {title, description}`
  const pushNewError = (oldArray: NotificationError[], newError: NotificationError) => {
    setErrors([...oldArray, { _id: `${oldArray.length}`, title: newError.title, type: newError.type, description: newError.description }])
  }

  // Get redirect location, co, and any error from the state passed by location
  const { errorMessage } = location.state || {}

  useEffect(() => {
    if (errorMessage !== undefined && errors.length < 1) pushNewError(errors, { title: 'auth.LOGIN_ERROR', type: 'danger', description: errorMessage, _id: '' })
  }, [errors, errorMessage])

  const LoginSchema = Yup.object().shape({
    username: Yup.string().notOneOf([co], 'auth.USERNAME_CANT_BE_COMPANY').min(2, 'auth.TOO_SHORT').max(70, 'auth.TOO_LONG').required('auth.REQUIRED'),
    password: Yup.string().min(2, 'auth.TOO_SHORT').max(70, 'auth.TOO_LONG').required('auth.REQUIRED')
  })

  const initialValues: InitialValues = {
    username: '',
    password: ''
  }

  const onSubmit = async (values: InitialValues, actions: FormikHelpers<InitialValues>) => {
    const { username, password } = values
    try {
      const res = await Auth.post('', { credentials: { username, password, co } })
      if (!res.success) {
        actions.setSubmitting(false)
        // actions.setErrors(error)
        actions.setStatus({ msg: res.message })
      } else {
        await dispatch(setLoggedIn(res.token, res.renewToken))

        if (res.changePasswordNeeded) dispatch(openModal('ResetPasswordModal', {}))
      }
    } catch (err) {
      const error = err as AxiosError

      actions.setSubmitting(false)
      actions.setStatus({ msg: error?.message })
    }
  }

  // Return the login form
  return (
    <>
      {errors.length > 0 && (
        <>
          <Notifications notifications={errors} />
        </>
      )}
      <MerinioCard>
        <Card.Body>
          <Formik validationSchema={LoginSchema} initialValues={initialValues} onSubmit={onSubmit}>
            {({ status, isSubmitting, dirty, setStatus }) => (
              <Form dirty={!!dirty && !isSubmitting}>
                <Alert onClose={() => setStatus()} show={!!status?.msg}>
                  {status?.msg}
                </Alert>
                {/* Alert for confirmation that an email was sent to reset password or username*/}
                <Alert variant="success" onClose={() => setConfirmationAlert('')} show={!!confirmationAlert}>
                  {t(`auth.EMAIL_${confirmationAlert}_CONFIRMATION`)}
                </Alert>
                <Input type="text" name="username" module="auth" required autoComplete="username" autoCorrect="off" autoCapitalize="none" />
                <Input type="password" name="password" module="auth" autoComplete="current-password" required autoCorrect="off" autoCapitalize="none" />
                <div className="mb-4 d-flex">
                  <Button id="login-submit" type="submit" isSubmitting={isSubmitting} text="auth.SUBMIT" />
                  <div className="ml-auto">
                    <div>
                      <NavLink to="/forgot/username">
                        <Trans t={t} i18nKey="auth.FORGOT_USERNAME"></Trans>
                      </NavLink>
                    </div>
                    <div>
                      <NavLink to="/forgot/password">{t('auth.FORGOT_PASSWORD')}</NavLink>
                    </div>
                  </div>
                </div>
                <Prompt when={dirty} message="Are you sure you want to leave?" />
              </Form>
            )}
          </Formik>
        </Card.Body>
      </MerinioCard>
    </>
  )
}

export default Login
